import { useCallback, useEffect, useState } from 'react'
import { Network } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { formatDate } from '@/utils/format-date'
import { useAuthStore } from '@/stores/authStore'
import { canManagePartnerCommissions } from '@/utils/permissions'
import {
  DEFAULT_INSTALLMENT_PLAN,
  describeInstallmentPlan,
} from '@/components/Partners/Commission/PartnerInstallmentPlanFields'
import { RP_CONTRACT_LOCK, standardForPartnerTipo } from '@/components/Comissoes/commercialStandard'
import type { Deal, DealPartnerPyramid } from '@/types'

export function DealPartnerCommissionSection({ deal }: { deal: Deal }) {
  const userRole = useAuthStore((s) => s.user?.role)
  const canManage = canManagePartnerCommissions(userRole)
  const [data, setData] = useState<DealPartnerPyramid | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(
    (signal?: AbortSignal) =>
      api
        .get(`/parceiros/deals/${deal.id}/pyramid`, { signal })
        .then((resp) => setData(resp.data.data ?? null))
        .catch(() => {
          if (!signal?.aborted) setData(null)
        }),
    [deal.id],
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load, deal.valorMensal, deal.etapa])

  if (!data?.parceiroId || data.chain.length === 0) return null

  const mode = data.mode ?? 'RECORRENTE'
  const isRecurring = mode === 'RECORRENTE'
  const recurrenceEnded = Boolean(data.recorrenciaEncerradaEm)

  const plan = data.plano ?? DEFAULT_INSTALLMENT_PLAN
  const standard = standardForPartnerTipo(data.partnerTipo)

  const toggleRecurrence = async () => {
    const action = recurrenceEnded ? 'reopen' : 'end'
    if (action === 'end' && !window.confirm('Encerrar a recorrência? Novas comissões mensais deixam de ser geradas.')) return
    setSaving(true)
    try {
      await api.post(`/parceiros/deals/${deal.id}/recurrence/${action}`, {})
      await load()
      toast.success(action === 'end' ? 'Recorrência encerrada' : 'Recorrência reaberta')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao atualizar recorrência'))
    } finally {
      setSaving(false)
    }
  }

  const total = data.chain.reduce(
    (sum, level) => sum + (isRecurring ? level.valorRecorrente : level.valorAlternativo),
    0,
  )

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-text-label">
          <Network size={14} className="text-accent" />
          Comissão de parceiros
        </h3>
        {data.frozen && <span className="text-[10px] text-text-label">Cadeia congelada na assinatura</span>}
      </div>

      <p className="text-[12px] font-medium text-text-primary">{standard.title}</p>
      <p className="text-[11px] leading-relaxed text-text-label">{standard.detail}</p>
      <p className="text-[11px] leading-relaxed text-text-primary">{standard.commercial}</p>
      {['RP', 'INDICADOR'].includes((data.partnerTipo ?? '').toUpperCase()) && (
        <p className="text-[11px] leading-relaxed text-text-primary">{RP_CONTRACT_LOCK}</p>
      )}

      {!isRecurring && (
        <div className="space-y-1">
          <p className="text-[11px] text-text-primary">{describeInstallmentPlan(plan)}.</p>
          {data.parcelasAlternativo?.map((p) => (
            <p key={p.parcela} className="text-[11px] text-text-label">
              {p.parcela}º pagamento → {p.nome} (N{p.nivel})
            </p>
          ))}
        </div>
      )}

      {data.valorMensal <= 0 ? (
        <p className="text-[11px] text-amber-400">Defina o valor mensal do deal para calcular as comissões.</p>
      ) : (
        <div className="divide-y divide-white/[0.06] rounded-lg border border-white/[0.06]">
          {data.chain.map((level) => (
            <div key={level.parceiroId} className="flex items-center justify-between px-3 py-2 text-xs">
              <span className="flex items-center gap-2">
                <span className="rounded bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-bold text-text-muted">N{level.nivel}</span>
                <span className="text-text-primary">{level.nome}</span>
                {isRecurring && <span className="text-text-label">{level.percentual}%</span>}
              </span>
              <span className="font-semibold text-emerald-400">
                {formatCurrency(isRecurring ? level.valorRecorrente : level.valorAlternativo)}
                {isRecurring && <span className="font-normal text-text-label">/mês</span>}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold">
            <span className="text-text-label">
              Total{isRecurring && data.totalPercent != null ? ` · ${data.totalPercent}% da mensalidade` : ''}
            </span>
            <span className="text-text-primary">
              {formatCurrency(total)}
              <span className="font-normal text-text-label">
                {isRecurring ? '/mês' : ` · ${plan.parcelas} mensalidade(s)`}
              </span>
            </span>
          </div>
        </div>
      )}

      {deal.etapa === 'ganho' && isRecurring && (
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-text-label">
            {recurrenceEnded
              ? `Recorrência encerrada em ${formatDate(data.recorrenciaEncerradaEm as string)}`
              : 'Recorrência ativa'}
          </span>
          {canManage && (
            <button
              type="button"
              disabled={saving}
              onClick={toggleRecurrence}
              className={cn('font-medium hover:underline', recurrenceEnded ? 'text-accent' : 'text-red-400')}
            >
              {recurrenceEnded ? 'Reabrir' : 'Encerrar recorrência'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
